import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useNotification } from 'hooks/ui/useNotification';
import { api } from 'lib/httpClient';
import { Api } from 'further-types/organisation-user';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';

async function listOrgUsers({ queryKey }) {
  const [, organisationId] = queryKey;

  const {
    data: { data },
  } = await api.get(`organisation/${organisationId}/user/list`);
  return data;
}
interface Params {
  organisationId: string;
}

export function useListOrganisationUsers({ organisationId }: Params) {
  return useQuery<Api.ListOrganisationUsersResponse>({
    queryKey: ['organisation-users', organisationId],
    queryFn: listOrgUsers,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!organisationId,
  });
}

export function useDeleteOrganisationUser({ organisationId }: Params) {
  const queryClient = useQueryClient();
  const message = useNotification();

  return useMutation({
    mutationFn: (orgUserId: string) => {
      return api.delete(`organisation/user/${orgUserId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organisation-users', organisationId],
      });
      message.success('Organisation user deleted successfully.');
    },
    onError: () => {
      message.error('Error deleting organisation user.');
    },
  });
}

export function useCreateOrganisationUser({ organisationId }: Params) {
  const queryClient = useQueryClient();
  const message = useNotification();

  return useMutation({
    mutationFn: (data: Api.CreateOrganisationUserParams) => {
      return api.post(`organisation/user`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organisation-users', organisationId],
      });
      message.success('Organisation user created successfully.');
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.responseMsg ||
          'Error creating organisation user.',
      );
    },
  });
}

export function useUpdateOrganisationUserFirms({ organisationId }: Params) {
  const queryClient = useQueryClient();
  const message = useNotification();
  const handleApiException = useApiExceptionHandler();

  return useMutation({
    mutationFn: ({
      orgUserId,
      firmIds,
      hasAllFirmsAccess,
    }: {
      orgUserId: string;
      firmIds: Array<string>;
      hasAllFirmsAccess: boolean;
    }) => {
      return api.put(`organisation/user/${orgUserId}/firms`, {
        firmIds,
        hasAllFirmsAccess,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organisation-users', organisationId],
      });
      message.success('Firm access updated successfully.');
    },
    onError: (error: Error) =>
      handleApiException(error, 'Error updating firm access.'),
  });
}

export function useRevokeFirmAccess({ organisationId }: Params) {
  const queryClient = useQueryClient();
  const message = useNotification();
  const handleApiException = useApiExceptionHandler();

  return useMutation({
    mutationFn: (orgUserId: string) => {
      return api.post(`organisation/user/${orgUserId}/revoke-firm-access`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organisation-users', organisationId],
      });
      message.success('Firm access revoked successfully.');
    },
    onError: (error: Error) =>
      handleApiException(error, 'Error revoking firm access.'),
  });
}

export function useGrantFirmAccess({ organisationId }: Params) {
  const queryClient = useQueryClient();
  const message = useNotification();
  const handleApiException = useApiExceptionHandler();

  return useMutation({
    mutationFn: (orgUserId: string) => {
      return api.post(`organisation/user/${orgUserId}/grant-firm-access`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organisation-users', organisationId],
      });
      message.success('Firm access granted successfully.');
    },
    onError: (error: Error) =>
      handleApiException(error, 'Error granting firm access.'),
  });
}

export function useOrganisationUser(params: Params) {
  return {
    create: useCreateOrganisationUser(params),
    remove: useDeleteOrganisationUser(params),
  };
}
